export const PWA_STARTUP_STORAGE_KEYS = {
  firstRunDone: 'ac_pwa_first_run_done',
  pendingActivationToken: 'ac_activation_token_pending',
  pendingActivationCode: 'ac_activation_code_pending',
} as const;

export type PwaStartupAction =
  | 'none'
  | 'mark-first-run'
  | 'redirect-activation'
  | 'redirect-security-compliance';

export type PwaStartupReason =
  | 'not-standalone'
  | 'already-resolved'
  | 'missing-session-token'
  | 'pending-activation'
  | 'already-on-activation'
  | 'first-run-security-compliance'
  | 'already-on-security-compliance';

export type PwaStartupPendingActivation = {
  token: string;
  code: string;
};

export type PwaStartupResolverInput = {
  isStandalone: boolean;
  hasSessionToken?: boolean;
  hasAuthToken?: boolean;
  firstRunDone: boolean;
  currentPath?: string | null;
  pendingActivationToken?: string | null;
  pendingActivationCode?: string | null;
  hasPendingActivationToken?: boolean;
  activationPath?: string;
  securityCompliancePath?: string;
};

export type PwaStartupDecision = {
  action: PwaStartupAction;
  reason: PwaStartupReason;
  redirectTo: string | null;
  targetPath: string | null;
  shouldMarkFirstRunDone: boolean;
  markFirstRunDone: boolean;
  requiresAuthCheck: boolean;
  consumePendingActivation: boolean;
  pendingActivation: PwaStartupPendingActivation | null;
};

const DEFAULT_ACTIVATION_PATH = '/ativar';
const DEFAULT_SECURITY_COMPLIANCE_PATH = '/security-compliance';

export function resolvePwaStartup(input: PwaStartupResolverInput): PwaStartupDecision {
  const activationPath = normalizeRoutePath(input.activationPath, DEFAULT_ACTIVATION_PATH);
  const securityCompliancePath = normalizeRoutePath(
    input.securityCompliancePath,
    DEFAULT_SECURITY_COMPLIANCE_PATH,
  );
  const currentPath = normalizeCurrentPath(input.currentPath);
  const pendingActivation = getPendingActivation(input);
  const hasPendingActivation = pendingActivation !== null || input.hasPendingActivationToken === true;
  const hasSessionToken = input.hasSessionToken === true || input.hasAuthToken === true;

  if (!input.isStandalone) {
    return createDecision('none', 'not-standalone');
  }

  if (hasPendingActivation) {
    if (isSameRouteOrNested(currentPath, activationPath)) {
      return createDecision('mark-first-run', 'already-on-activation', {
        shouldMarkFirstRunDone: !input.firstRunDone,
      });
    }

    return createDecision('redirect-activation', 'pending-activation', {
      redirectTo: pendingActivation
        ? buildActivationRedirect(activationPath, pendingActivation)
        : activationPath,
      shouldMarkFirstRunDone: !input.firstRunDone,
      consumePendingActivation: true,
      pendingActivation,
    });
  }

  if (input.firstRunDone) {
    return createDecision('none', 'already-resolved');
  }

  if (!hasSessionToken) {
    return createDecision('none', 'missing-session-token');
  }

  if (isSameRouteOrNested(currentPath, securityCompliancePath)) {
    return createDecision('mark-first-run', 'already-on-security-compliance', {
      shouldMarkFirstRunDone: true,
    });
  }

  return createDecision('redirect-security-compliance', 'first-run-security-compliance', {
    redirectTo: securityCompliancePath,
    shouldMarkFirstRunDone: true,
    requiresAuthCheck: true,
  });
}

function createDecision(
  action: PwaStartupAction,
  reason: PwaStartupReason,
  overrides: Partial<PwaStartupDecision> = {},
): PwaStartupDecision {
  const decision = {
    action,
    reason,
    redirectTo: null,
    targetPath: null,
    shouldMarkFirstRunDone: false,
    markFirstRunDone: false,
    requiresAuthCheck: false,
    consumePendingActivation: false,
    pendingActivation: null,
    ...overrides,
  };
  const redirectTo = decision.redirectTo ?? decision.targetPath ?? null;
  const shouldMarkFirstRunDone =
    decision.shouldMarkFirstRunDone === true || decision.markFirstRunDone === true;

  return {
    ...decision,
    redirectTo,
    targetPath: decision.targetPath ?? redirectTo,
    shouldMarkFirstRunDone,
    markFirstRunDone: shouldMarkFirstRunDone,
  };
}

function getPendingActivation(input: PwaStartupResolverInput): PwaStartupPendingActivation | null {
  const token = normalizeOptionalValue(input.pendingActivationToken);
  if (!token) return null;

  return {
    token,
    code: normalizeOptionalValue(input.pendingActivationCode),
  };
}

function buildActivationRedirect(path: string, pendingActivation: PwaStartupPendingActivation): string {
  const params = new URLSearchParams();
  params.set('token', pendingActivation.token);
  if (pendingActivation.code) params.set('code', pendingActivation.code);
  return `${path}?${params.toString()}`;
}

function normalizeOptionalValue(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeRoutePath(value: string | null | undefined, fallback: string): string {
  const normalized = normalizeCurrentPath(value);
  if (!normalized || !normalized.startsWith('/') || normalized.startsWith('//')) return fallback;
  return normalized;
}

function normalizeCurrentPath(value: string | null | undefined): string {
  const raw = normalizeOptionalValue(value);
  if (!raw) return '';
  return raw.split(/[?#]/, 1)[0] || '';
}

function isSameRouteOrNested(currentPath: string, targetPath: string): boolean {
  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
}
